import { percent, px, rem } from 'csx';
import React from 'react';
import { merge } from 'lodash';
import { classes, extend, style } from 'typestyle';
import {
  colorBlack,
  colorBrand,
  colorContrast,
  colorDarkBlueGrey,
  colorFavouriteHover,
  colorGreyAccent,
  colorLightBlueGrey,
  colorSlate,
  colorWhite,
} from '../../theme/color';
import { fontTitles } from '../../theme/font';
import { mixinTextEllipsis, overlayLink } from '../../theme/mixin';
import { SVGIcon } from '../svg-icon';
import { ButtonPill } from '../button-pill';
import { transitionQuickEase } from '../../theme/transition';
import { constantTileCarouselDesktopSize, constantTileCarouselMobileSize } from '../../theme/constant';
import { mediaTablet } from '../../theme/media';
import { Component, deps, inject, observer } from '../../lib/component';
import { animationPulse } from '../../theme/animation';
import { EntityImageSchema } from '../../types/schema';
import FavouritePlaylistButtonCompat from "../compat/favourite-playlist-button-compat";

export const CarouselPlaylistTileStyles = style(
  {
    width: constantTileCarouselMobileSize,
    marginRight: px(16),
  },
  mediaTablet({
    width: constantTileCarouselDesktopSize,
  }),
);

export const CompactPlaylistTileStyles = style({
  $nest: {
    '& .title-banner': {
      position: 'absolute',
      bottom: 0,
      width: percent(100),
      height: 'auto',
      backgroundColor: colorBlack.fade(0.9).toString(),
    },
    '& .title-link': {
      color: colorWhite.toHexString(),
      lineHeight: 1,
      padding: rem(0.5),
      $nest: {
        '&:hover, &:focus': {
          color: colorWhite.toHexString(),
          textDecoration: 'underline',
        }
      }
    },
  },
});

const styles = {
  tile: style({
    position: 'relative',
  }),
  favouriteButton: style({
    position: 'absolute',
    top: px(8),
    right: px(8),
    zIndex: 2,
  }),
  images: style({
    display: 'flex',
    flexWrap: 'wrap',
    $nest: {
      '& img': {
        width: percent(50),
      },
    },
  }),
  title: style({
    backgroundColor: colorLightBlueGrey.toHexString(),
    boxShadow: `0 1px 0 0 ${colorGreyAccent.toHexString()}`,
    height: px(55),
  }),
  titleText: style(
    merge(overlayLink, mixinTextEllipsis, {
      lineHeight: px(55),
      fontFamily: fontTitles,
      fontWeight: 'bold',
      padding: '0 8px',
      display: 'block',
      color: colorSlate.toHexString(),
      $nest: {
        '&:hover': {
          color: colorBrand.toHexString(),
        },
      },
    }),
  ),
};

export interface PlaylistTileProps {
  className?: string;
  identity: string;
  title: string;
  slug: string;
  images: EntityImageSchema[];
  onClickLink(event: React.MouseEvent<any>): void;
}

export default class PlaylistTile extends React.Component<PlaylistTileProps> {

  render() {
    const { className, identity, title, slug, images = [], onClickLink } = this.props;

    return (
      <div className={classes(styles.tile, className || CarouselPlaylistTileStyles)}>
        <FavouritePlaylistButtonCompat className={styles.favouriteButton} identity={identity} />
        <div className={styles.images}>
          {images.map(({ uuid }, key) => (
            <img src={`${process.env.APP_PROTOCOL}//${process.env.IMAGES_URL}/image/${uuid}-270x0.jpg`} key={key} />
          ))}
        </div>
        <div className={classes(styles.title, 'title-banner')}>
          <a
            className={classes(styles.titleText, 'title-link')}
            href={`/browse/collection/${slug}`}
            onClick={onClickLink}
          >
            {title}
          </a>
        </div>
      </div>
    );
  }
}
